import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { AcademicHolidaysService } from "./academic-holidays.service";
import { TranslateService } from "@ngx-translate/core";


declare var customToastr: any;
@Component({
    selector: "app-academic-holidays",
    templateUrl: "./academic-holidays.component.html",
    styleUrls: [
        "./academic-holidays.component.css",
        "./../../assets/css/theme-default/bootstrap.css",
        "./../../assets/css/theme-default/materialadmin.css",
        "./../../assets/css/theme-default/font-awesome.min.css",
        "./../../assets/css/theme-default/material-design-iconic-font.min.css",
        "./../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css",
        "./../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/buttons.dataTables.min.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css",
        "./../../_crop/css/style.css",
        "./../../_crop/css/style-example.css",
    ],
    encapsulation: ViewEncapsulation.None,
})

export class AcademicHolidaysComponent implements OnInit {
    isPageLoading = false;
    isDropdownOpen = false;
    allSelected = false;
    showOverlay = false;
    isCreatePopupVisible = false;
    isEditPopupVisible = false;
    isDeletePopupVisible = false;
    academicHolidayForm: FormGroup;
    academicHolidayFormSubmit = false;
    academicStartDate: string;
    academicEndDate: string;
    language = "en";
    selectedHolidayId = null;
    selectedClassId = [];
    academicHolidaysList = [];
    fullClassData = [];
    classDropdownDataArray = [];

    constructor(
        private router: Router,
        private academicHolidaysService: AcademicHolidaysService,
        private fb: FormBuilder,
        private translate: TranslateService
    ) {
        this.academicHolidayForm = this.fb.group({
            academicHolidayName: [
                { value: "", disabled: false },
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(200),
                    // this.alphaNumericWithSpacesValidator
                ],
            ],
            startDate: [
                { value: "", disabled: false },
                [Validators.required]
            ],
            endDate: [
                { value: "", disabled: false },
                [Validators.required]
            ],
            academicHolidayDescription: [
                { value: "", disabled: false },
                [
                    Validators.minLength(5),
                    Validators.maxLength(500),
                    this.checkSpaceInString
                ]
            ],
            selectedClasses: [
                { value: "", disabled: false },
                [Validators.required]
            ]
        })
    }

    ngOnInit(): void {
        this.featureCheck();
        this.getClassList();
        this.getHolidayList();
    }

    // function to check white space in string
    checkSpaceInString(control: AbstractControl) {
        if (control.value) {
            if (control.value.trim() === '') return { invalidCharacter: true }
        }
    }

    // function for convert the data for the class dropdown
    convertDataforClassDropdown() {
        this.classDropdownDataArray = [];
        this.fullClassData.forEach(category => {
            category.classStandards.forEach(classes => {
                classes.classes.forEach(standard => {
                    this.classDropdownDataArray.push({
                        classCategoryName: category.classCategoryName,
                        classStandardName: classes.classStandardName,
                        classDivisionName: standard.classDivision,
                        displayName: `${category.classCategoryName} - ${classes.classStandardName} - ${standard.classDivision}`,
                        categoryId: category.categoryId,
                        pkClassStandardsId: standard.classId
                    });
                });
            });
        });
    }

    // function to select all classes in the dropdown
    selectAllClasses() {
        this.academicHolidayForm.markAsDirty();
        this.selectedClassId = [];
        const allClasses: any = this.classDropdownDataArray
            .filter(item => !item.items)
            .map(item => item);
        this.academicHolidayForm.get('selectedClasses').setValue(allClasses);
    }

    // On select add the class id in the array
    onSelectionChange(event: any) {
        this.academicHolidayForm.markAsDirty();
        const uniqueClassIds = [];
        event.forEach(ele => {
            if (!uniqueClassIds.includes(ele.pkClassStandardsId)) {
                uniqueClassIds.push(ele.pkClassStandardsId);
            }
        });
        this.selectedClassId = uniqueClassIds;
    }

    // function for check the academic calendar feature is enable or not 
    featureCheck() {
        const extensionServices = localStorage.getItem('extensionServices');
        const data = JSON.parse(extensionServices);
        const featureEnabled = data.some(item => item.serviceName === 'ACADEMIC_CALENDAR')
        featureEnabled == true ?
            this.router.navigate(["/academic/holidays"]) : this.router.navigate(["***"])
    }

    // function for check the string contains only alphabats and number and white spaces
    alphaNumericWithSpacesValidator(control: AbstractControl): { [key: string]: any } | null {
        const regex = /^[\p{L}\p{N}\s'\-]+$/u;
        if (!control.value) {
            return null;
        }
        const value = control.value.trim();
        if (value === '') return { invalidCharacter: true };
        if (value) {
            if (!regex.test(value)) {
                return { invalidCharacter: true };
            }
        }
        return null;
    }

    // Validate dates to enforce custom rules
    validateDates(): void {
        const startDateControl = this.academicHolidayForm.get('startDate');
        const endDateControl = this.academicHolidayForm.get('endDate');
        const startDate = startDateControl?.value ? new Date(startDateControl.value) : null;
        const endDate = endDateControl?.value ? new Date(endDateControl.value) : null;
        const academicStartDate = new Date(this.academicStartDate);
        const academicEndDate = new Date(this.academicEndDate);
        if (!startDate) {
            startDateControl?.setErrors({ required: true });
        }
        else if (startDate && academicStartDate && startDate < academicStartDate) {
            startDateControl?.setErrors({ invalidStartDate: true });
        }
        else {
            startDateControl?.setErrors(null);
        }
        if (!endDate) {
            endDateControl?.setErrors({ required: true })
        }
        else if (endDate && academicEndDate && endDate > academicEndDate) {
            endDateControl?.setErrors({ invalidEndDate: true });
        }
        else {
            endDateControl?.setErrors(null);
        }
        if (startDate && endDate && startDate > endDate) {
            startDateControl?.setErrors({ startDateMorethenEndDate: true });
        }
        this.academicHolidayForm.updateValueAndValidity();
    }

    // Call the API to fetch the class list
    async getClassList() {
        try {
            const response: any = await firstValueFrom(this.academicHolidaysService.getClassListService());
            if (response?.status) {
                this.fullClassData = response?.body?.classDetailList;
                this.convertDataforClassDropdown();
            }
        }
        catch (error) {
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
                customToastr(getApiErrorMessageTranslation, "error");
            }
        }
    }

    // function to call the API to fetch the holiday list
    async getHolidayList() {
        try {
            this.isPageLoading = true;
            const respone: any = await firstValueFrom(this.academicHolidaysService.getHolidayListService());
            this.isPageLoading = false;
            if (respone?.status) {
                this.academicHolidaysList = respone?.body?.holidayList;
                this.academicStartDate = respone?.body?.academicStartDate;
                this.academicEndDate = respone?.body?.academicEndDate;
            }
        }
        catch (error) {
            this.isPageLoading = false;
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
                customToastr(getApiErrorMessageTranslation, "error");
            }
        }
    }

    // function for the open the create holiday form
    openCreateHolidayForm() {
        this.isEditPopupVisible = false;
        this.showOverlay = true;
        this.isCreatePopupVisible = true;
    }

    // function for edit the holiday 
    openEditHolidayForm(data: any) {
        this.selectedHolidayId = data?.pkSchoolHolidaysId;
        const selectedClassObjects = this.classDropdownDataArray.filter(classItem =>
            data.classIds.some(id => id == classItem.pkClassStandardsId)
        );
        this.academicHolidayForm.patchValue({
            academicHolidayName: data.holidayTitle,
            academicHolidayDescription: data.description,
            startDate: data.StartDate,
            endDate: data.EndDate ? data.EndDate.split('T')[0] : null,
            selectedClasses: selectedClassObjects.length > 0 ? selectedClassObjects : data.classIds
        });
        this.isEditPopupVisible = true;
        this.showOverlay = true;
        this.academicHolidayForm.markAsPristine();
    }

    // function for open the delete holiday confirm box
    openDeleteHolidayForm(data: any) {
        this.selectedHolidayId = data?.pkSchoolHolidaysId
        this.isDeletePopupVisible = true;
        this.showOverlay = true;
        this.isCreatePopupVisible = false;
        this.isEditPopupVisible = false;
    }

    // function for disable the input form fields
    disableFormControls() {
        Object.keys(this.academicHolidayForm.controls).forEach((controlName) => {
            this.academicHolidayForm.get(controlName)?.disable();
        });
    }

    // function for enable the input form fields
    enableFormControls() {
        Object.keys(this.academicHolidayForm.controls).forEach((controlName) => {
            this.academicHolidayForm.get(controlName)?.enable();
        });
    }

    // function for delete the 
    async deleteAcademicHoliday() {
        try {
            const body = {
                "lang": "en",
                "academicId": this.academicHolidaysService.academicId,
                "holidayId": this.selectedHolidayId,
            }
            this.academicHolidayFormSubmit = true;
            const response: any = await firstValueFrom(this.academicHolidaysService.deleteHolidayService(body));
            this.academicHolidayFormSubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, "success");
                }
                else {
                    const deleteHolidaySuccessfullTranslation = await firstValueFrom(this.translate.get('deleteHolidaySuccessfull'));
                    customToastr(deleteHolidaySuccessfullTranslation, "success");
                }
                this.closePopup();
                this.getHolidayList();
            }
        }
        catch (error) {
            this.academicHolidayFormSubmit = false;
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const deleteAcademicHolidayErrorTranslation = await firstValueFrom(this.translate.get('deleteAcademicHolidayError'));
                customToastr(deleteAcademicHolidayErrorTranslation, "error");
            }
        }
    }

    // Function for submit the call the edit or create form on edit or create the holidays
    async onFormSubmit() {
        this.isEditPopupVisible ? this.editAcademicHolidayOnSubmit() : this.createAcademicHolidayOnSubmit();
    }

    // Create holiday API call function
    async createAcademicHolidayOnSubmit() {
        try {
            const selectedClasses = this.academicHolidayForm.get('selectedClasses').value;
            const classIds = Array.isArray(selectedClasses)
                ? selectedClasses.map(cls =>
                    cls && typeof cls === 'object'
                        ? cls.pkClassStandardsId
                        : cls
                ).filter(id => id != null)
                : [];
            if (this.academicHolidayForm.valid) {
                const body = {
                    academicId: this.academicHolidaysService.academicId,
                    lang: this.language,
                    title: this.academicHolidayForm
                        .get("academicHolidayName")
                        ?.value?.trim(),
                    startDate: this.academicHolidayForm
                        .get("startDate")
                        ?.value?.toString(),
                    endDate: this.academicHolidayForm
                        .get("endDate")
                        ?.value?.toString(),
                    description: this.academicHolidayForm
                        .get("academicHolidayDescription")
                        ?.value?.trim(),
                    selectedClasses: classIds
                }
                const description = body.description?.trim();
                if (!description) {
                    delete body.description;
                }
                this.academicHolidayFormSubmit = true;
                this.disableFormControls();
                const response: any = await firstValueFrom(this.academicHolidaysService.createHolidayService(body));
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, "success");
                    }
                    else {
                        const holidayCreatedSuccessfullyTranslation = await firstValueFrom(this.translate.get('holidayCreatedSuccessfully'));
                        customToastr(holidayCreatedSuccessfullyTranslation, "success");
                    }
                    this.closePopup();
                }
                this.academicHolidayFormSubmit = false;
                this.enableFormControls();
                this.getHolidayList();
            }
        }
        catch (error) {
            this.academicHolidayFormSubmit = false;
            this.enableFormControls();
            const somethingWentWrongMessageTranslation = await firstValueFrom(this.translate.get('somethingWentWrongMessage'));
            (error?.error?.message) ? customToastr(error?.error?.message, "error") : customToastr(somethingWentWrongMessageTranslation, "error");
        }
    }

    // function for edit the holiday
    async editAcademicHolidayOnSubmit() {
        try {
            const selectedClasses = this.academicHolidayForm.get('selectedClasses').value;
            const classIds = Array.isArray(selectedClasses)
                ? selectedClasses.map(cls =>
                    cls && typeof cls === 'object'
                        ? cls.pkClassStandardsId
                        : cls
                ).filter(id => id != null)
                : [];
            const body = {
                academicId: this.academicHolidaysService.academicId,
                holidayId: this.selectedHolidayId,
                lang: this.language,
                title: this.academicHolidayForm
                    .get("academicHolidayName")
                    ?.value?.trim(),
                description: this.academicHolidayForm
                    .get("academicHolidayDescription")
                    ?.value?.trim(),
                startDate: this.academicHolidayForm
                    .get("startDate")
                    ?.value?.toString(),
                endDate: this.academicHolidayForm
                    .get("endDate")
                    ?.value?.toString(),
                selectedClasses: classIds
            }
            const description = body.description?.trim();
            if (!description) {
                delete body.description;
            }
            if (this.academicHolidayForm.dirty) {
                this.academicHolidayFormSubmit = true;
                this.disableFormControls();
                const response: any = await firstValueFrom(this.academicHolidaysService.editHolidayService(body));
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, "success");
                    }
                    else {
                        const holidayUpdatedSuccessfullyTranslation = await firstValueFrom(this.translate.get('holidayUpdatedSuccessfully'));
                        customToastr(holidayUpdatedSuccessfullyTranslation, "success");
                    }
                    this.closePopup();
                }
                this.academicHolidayFormSubmit = false;
                this.enableFormControls();
                this.getHolidayList();
            }
        }
        catch (error) {
            this.enableFormControls();
            this.academicHolidayFormSubmit = false;
            const somethingWentWrongMessageTranslation = await firstValueFrom(this.translate.get('somethingWentWrongMessage'));

            (error?.error?.message) ? customToastr(error?.error?.message, "error") : customToastr(somethingWentWrongMessageTranslation, "error");
        }
    }

    // Function to close the create, edit and delete confirmation boxes
    closePopup() {
        this.isEditPopupVisible = this.isDeletePopupVisible = this.isCreatePopupVisible = this.showOverlay = false;
        this.academicHolidayForm.reset(
            {
                academicHolidayName: "",
                academicHolidayDescription: "",
                startDate: "",
                endDate: "",
                selectedClasses: []
            }
        );
        this.academicHolidayForm.get('selectedClasses')?.setValue("");
    }
}